import { FilterType, TreeFilterType, TypeSelections } from './types'
import React, { useEffect, useState } from 'react'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import {
  Box,
  Checkbox,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'

export function TypeSelect({
  filter,
  onFiltersChanged,
}: {
  filter: TreeFilterType
  onFiltersChanged(filters: TreeFilterType): void
}) {
  const [filterCopy, setFilterCopy] = useState<TypeSelections>({
    ...(filter.typeSelections ?? { filterType: 'including', types: [] }),
  })

  useEffect(() => {
    setFilterCopy(
      filter.typeSelections ?? { filterType: 'including', types: [] },
    )
  }, [filter])

  const options = Object.entries(
    filter.filterType === FilterType.node ? NodeType : LeafTypes,
  )

  const excludeFilter = (toExclude: string) => (toCheck: string) =>
    toCheck !== toExclude

  const setTypeSelected = (type: string, isSelected: boolean) => {
    setFilterCopy(({ filterType, types = [] }) => {
      return {
        filterType,
        types: isSelected
          ? [...types, type as NodeType | LeafTypes]
          : types.filter(excludeFilter(type)),
      }
    })
  }

  const toggleOption = (
    { target: { value } }: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setTypeSelected(value, checked)
  }

  const displayValue = `${filterCopy.filterType === 'excluding' && !!filterCopy.types?.length ? 'EXC' : 'INC'} ${filterCopy.types?.length || 'ALL'}`
  const TypeOption = ({
    display,
    optionValue,
  }: {
    display: string
    optionValue: NodeType | LeafTypes
  }) => {
    return (
      <MenuItem
        value={optionValue}
        onClick={() => {
          setTypeSelected(optionValue, !filterCopy.types?.includes(optionValue))
        }}
      >
        <Box
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>{display}</Box>
          <Box>
            <Checkbox
              inputProps={
                {
                  'data-testid': `${optionValue}-toggle`,
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
              value={optionValue}
              checked={filterCopy.types?.includes(optionValue)}
            />
          </Box>
        </Box>
      </MenuItem>
    )
  }

  const doIt = () => {
    onFiltersChanged({
      ...filter,
      typeSelections: {
        filterType: filterCopy.filterType ?? 'including',
        types: filterCopy.types ?? [],
      },
    })
  }

  return (
    <Box>
      <Select
        data-testid="typeSelect"
        onClose={doIt}
        displayEmpty={true}
        renderValue={() => displayValue || 'All Types'}
      >
        <ToggleButtonGroup
          exclusive={true}
          size="small"
          value={filterCopy.filterType || 'including'}
          onChange={(_: unknown, value: 'including' | 'excluding') =>
            setFilterCopy(({ types }) => ({ types, filterType: value }))
          }
        >
          <ToggleButton
            data-testid="include-toggle"
            selected={filterCopy.filterType === 'including'}
            value="including"
          >
            Including
          </ToggleButton>
          <ToggleButton
            data-testid="exclude-toggle"
            selected={filterCopy.filterType === 'excluding'}
            value="excluding"
          >
            Excluding
          </ToggleButton>
        </ToggleButtonGroup>
        {options.map(([display, value]) => {
          return (
            <TypeOption key={value} optionValue={value} display={display} />
          )
        })}
      </Select>
    </Box>
  )
}
